// src/components/common/UI.js
import './UI.css';
import { useLanguage } from '../../contexts/LanguageContext';
import { motion } from 'framer-motion';

export const LoadingSpinner = () => {
  const { translate } = useLanguage();

  // Container animation
  const containerVariants = {
    start: {
      transition: {
        staggerChildren: 0.15
      }
    },
    end: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  // Circle animation
  const circleVariants = {
    start: {
      y: 0,
      scale: 1,
      opacity: 0.7
    },
    end: {
      y: -20,
      scale: 1.2,
      opacity: 1
    }
  };

  const circleTransition = {
    duration: 0.6,
    repeat: Infinity,
    repeatType: "reverse",
    ease: "easeInOut"
  };

  return (
    <div className="tab-body" style={{
      minHeight: "500px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: 'center',
      gap: '32px',
      border: 'none',
      boxShadow: 'none'
    }}>
      {/* Modern Loading Animation */}
      <motion.div
        style={{
          display: 'flex',
          gap: '12px',
          alignItems: 'center',
          height: '60px'
        }}
        variants={containerVariants}
        initial="start"
        animate="end"
      >
        {[0, 1, 2, 3, 4].map((index) => (
          <motion.div
            key={index}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: `linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)`,
              boxShadow: '0 2px 8px rgba(59, 130, 246, 0.3)'
            }}
            variants={circleVariants}
            transition={{
              ...circleTransition,
              delay: index * 0.1
            }}
          />
        ))}
      </motion.div>

      {/* Loading Text with fade animation */}
      <motion.span
        style={{
          fontSize: '1rem',
          color: '#6b7280',
          fontWeight: '500',
          letterSpacing: '0.5px'
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.8,
          ease: "easeOut"
        }}
      >
        {translate('common.loadingText')}
      </motion.span>
    </div>
  );
};

// Clean, modern Error Message Component with timing-based error detection
export const ErrorMessage = ({ error, onRetry, onResetFilters }) => {
  const { translate } = useLanguage();

  // Determine error type - prioritize explicit flags
  const isNoData = error?.isNoData || false;
  const isTimeout = error?.isTimeout || false;
  const errorMessage = error?.message || error || translate('common.error');

  // Get appropriate error config
  const errorType = isTimeout ? 'timeout' : 'noData';
  const errorConfig = translate(`common.errors.${errorType}`) || {};

  // Special styling for "no data" - clean and simple with purple theme
  if (isNoData || (!isTimeout && !error?.isTimeout)) {
    return (
      <div style={{
        minHeight: "500px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "40px 20px"
      }}>
        <div style={{
          maxWidth: "420px",
          width: "100%",
          textAlign: "center"
        }}>
          {/* Simple Icon */}
          <div style={{
            fontSize: "3rem",
            marginBottom: "24px",
            opacity: 0.5
          }}>
            🔍
          </div>

          {/* Title */}
          <h3 style={{
            fontSize: "1.5rem",
            fontWeight: "600",
            color: "#667eea",
            marginBottom: "12px"
          }}>
            {errorConfig.title || translate('common.errors.noData.title')}
          </h3>

          {/* Message */}
          <p style={{
            fontSize: "1rem",
            color: "#6b7280",
            lineHeight: "1.6",
            marginBottom: "20px"
          }}>
            {errorConfig.message || translate('common.errors.noData.message')}
          </p>

          {/* Suggestion */}
          {errorConfig.suggestion && (
            <p style={{
              fontSize: "0.9rem",
              color: "#9ca3af",
              marginBottom: "32px"
            }}>
              💡 {errorConfig.suggestion}
            </p>
          )}

          {/* Action Buttons */}
          <div style={{
            display: "flex",
            gap: "12px",
            justifyContent: "center"
          }}>
            <button
              onClick={onRetry}
              style={{
                padding: "12px 28px",
                backgroundColor: "#667eea",
                color: "white",
                border: "none",
                borderRadius: "8px",
                fontSize: "0.95rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#5568d3";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#667eea";
              }}
            >
              {translate('common.actions.retry')}
            </button>

            {onResetFilters && (
              <button
                onClick={onResetFilters}
                style={{
                  padding: "12px 28px",
                  backgroundColor: "white",
                  color: "#667eea",
                  border: "1px solid #e5e7eb",
                  borderRadius: "8px",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "#f9fafb";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "white";
                }}
              >
                {translate('common.actions.resetFilters')}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Timeout or other errors - simpler design
  return (
    <div style={{
      minHeight: "500px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: "20px"
    }}>
      <div style={{
        maxWidth: "480px",
        width: "100%",
        textAlign: "center"
      }}>
        {/* Icon */}
        <div style={{
          fontSize: "3.5rem",
          marginBottom: "24px",
          opacity: 0.7
        }}>
          ⏱️
        </div>

        {/* Title */}
        <h3 style={{
          fontSize: "1.5rem",
          fontWeight: "600",
          color: "#1f2937",
          marginBottom: "12px",
          letterSpacing: "-0.02em"
        }}>
          {errorConfig.title || translate('common.error')}
        </h3>

        {/* Error Message */}
        <p style={{
          fontSize: "1rem",
          color: "#6b7280",
          lineHeight: "1.6",
          marginBottom: "8px"
        }}>
          {errorConfig.message || errorMessage}
        </p>

        {/* Suggestion */}
        {errorConfig.suggestion && (
          <p style={{
            fontSize: "0.9rem",
            color: "#9ca3af",
            lineHeight: "1.5",
            marginBottom: "32px",
            fontStyle: "italic"
          }}>
            💡 {errorConfig.suggestion}
          </p>
        )}

        {/* Action Buttons */}
        <div style={{
          display: "flex",
          gap: "12px",
          justifyContent: "center",
          marginTop: "24px"
        }}>
          <button
            onClick={onRetry}
            style={{
              padding: "12px 24px",
              backgroundColor: "#3b82f6",
              color: "white",
              border: "none",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#2563eb";
              e.currentTarget.style.boxShadow = "0 4px 6px rgba(59,130,246,0.3)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#3b82f6";
              e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.1)";
            }}
          >
            {translate('common.actions.retry')}
          </button>

          {onResetFilters && (
            <button
              onClick={onResetFilters}
              style={{
                padding: "12px 24px",
                backgroundColor: "white",
                color: "#6b7280",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "0.95rem",
                fontWeight: "500",
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f9fafb";
                e.currentTarget.style.borderColor = "#9ca3af";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "white";
                e.currentTarget.style.borderColor = "#d1d5db";
              }}
            >
              {translate('common.actions.resetFilters')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export const NoData = () => (
  <div className="loading-container" style={{
    minHeight: "500px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: 'center',
    gap: '25px'
  }}>
    {/* <div className="no-data-icon">📭</div> */}
    <div className="no-data-message" style={{
      fontSize: '1.1rem',
      color: '#666'
    }}>
      데이터가 없습니다.
    </div>
  </div>
);

export const ContentLoadingSpinner = () => {
  const spinnerVariants = {
    start: { rotate: 0 },
    end: { rotate: 360 }
  };

  return (
    <div className="content-loading-container" style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '20px',
      padding: '40px'
    }}>
      <motion.div
        style={{
          width: '40px',
          height: '40px',
          border: '3px solid #e5e7eb',
          borderTop: '3px solid #3b82f6',
          borderRadius: '50%'
        }}
        variants={spinnerVariants}
        initial="start"
        animate="end"
        transition={{
          duration: 0.8,
          repeat: Infinity,
          ease: "linear"
        }}
      />
      <motion.span
        style={{
          fontSize: '0.9rem',
          color: '#6b7280',
          fontWeight: '500'
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        Loading...
      </motion.span>
    </div>
  );
};

export const ContentErrorMessage = ({ error }) => (
  <div className="loading-container" style={{
    minHeight: "500px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: 'center',
    gap: '30px'
  }}>
    <div className="error-icon">📝</div>
    <div className="error-message" style={{
      fontSize: '1.2rem',
      color: '#495057',
      lineHeight: '1.5',
      maxWidth: '80%',
      backgroundColor: '#fff5f5',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(255, 135, 135, 0.15)'
    }}>
      {error || '컨텐츠를 불러오는 중 문제가 발생했습니다.'}
    </div>
  </div>
);

export const NoContent = () => (
  <div className="loading-container" style={{
    minHeight: "500px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: 'center',
    gap: '25px'
  }}>
    <div className="no-content-icon">📭</div>
    <div className="no-content-message" style={{
      fontSize: '1.1rem',
      color: '#666'
    }}>
      등록된 컨텐츠가 없습니다.
    </div>
  </div>
);



export const LoadingSkeleton = () => {
  const { translate } = useLanguage();
  const pulseVariants = {
    start: { opacity: 0.4 },
    end: { opacity: 1 }
  };

  return (
    <div className="" style={{
      minHeight: "400px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      textAlign: 'center'
    }}>
      <div className="loading-content" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px'
      }}>
        <motion.div
          style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%'
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.span
          style={{
            fontSize: '1rem',
            color: '#6b7280',
            fontWeight: '500'
          }}
          variants={pulseVariants}
          initial="start"
          animate="end"
          transition={{
            duration: 1,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          {translate('common.loadingData')}
        </motion.span>
      </div>
    </div>
  );
};




export const BrandDetailLoadingSkeleton = () => {
  const { translate } = useLanguage();
  const pulseVariants = {
    start: { opacity: 0.4 },
    end: { opacity: 1 }
  };

  return (
    <div className="" style={{
      marginTop: "270px",
      minHeight: "400px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      textAlign: 'center',
      gap: '25px'
    }}>
      <div className="loading-content" style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '24px'
      }}>
        <motion.div
          style={{
            width: '50px',
            height: '50px',
            border: '4px solid #e5e7eb',
            borderTop: '4px solid #3b82f6',
            borderRadius: '50%'
          }}
          animate={{ rotate: 360 }}
          transition={{
            duration: 1,
            repeat: Infinity,
            ease: "linear"
          }}
        />
        <motion.span
          style={{
            fontSize: '1rem',
            color: '#6b7280',
            fontWeight: '500'
          }}
          variants={pulseVariants}
          initial="start"
          animate="end"
          transition={{
            duration: 1,
            repeat: Infinity,
            repeatType: "reverse"
          }}
        >
          {translate('common.loadingBrandInfo')}
        </motion.span>
      </div>
    </div>
  );
};